import {jsx, ParamComponent} from 'panel';
import {selx} from 'clsx';
import uniqBy from 'lodash/uniqBy';

import {NoExtension, Section, SectionItem} from 'common/components/items-menu/types';
import {
  getOrderedSearchContextDisplayOptions,
  getReportNameFromSearchContext,
  getSearchIconFromDisplayContext,
  getSuggestedCreators,
} from 'common/report/util/search';
import {SearchDisplayContext, SearchDisplayContextDisplayName} from 'common/report/util/search/types';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {ItemsMenuEl} from 'common/components/items-menu';
import {getDashboardAllowedReports} from 'common/report/util/boards';
import {findKeyByValue} from 'common/util/typescript';
import {IronContextRegistry} from 'common/report/context-registry';
import {TopCreator} from 'common/report/queries/app-api/project-top-creators';
import {UserSelectorEl} from 'common/widgets/user-selector';
import {userCookieUtils} from 'common/report/util/user-identity-from-cookie';
import {BrowseTrackedEvent} from 'common/report/util/search/tracking';
import {getProjectId, getWorkspaceId} from 'common/report/util';
import {QueryEventEntry, RowLabelType} from 'common/query-builder/query-entry-section/types';
import {ResourceType} from 'common/report/constants';
import {QueryEntrySectionEl} from 'common/query-builder/query-entry-section';
import {ButtonTheme} from 'common/components/button/constants';

import {FilterType, SearchFilter, ShareStatusFilter} from '../types';
import styles from './index.module.styl';

type BrowseFilterJsxProps = {
  params: BrowseFilterParams;
};

type BrowseFilterParams = {
  filter: SearchFilter;
  onFilterChange: (filter: SearchFilter) => void;
  onTrackEvent?: (event: BrowseTrackedEvent, props?: Record<string, any>) => void;
  isAddMode: boolean;
  userId: number;
  isSocialMetadataEnabled: boolean;
};

type OwnerFilterItem = SectionItem<ShareStatusFilter, {filterType: FilterType.ShareStatus}>;
type ReportFilterItem = SectionItem<SearchDisplayContext, {filterType: FilterType.Report}>;
type CreatorFilterItem = SectionItem<string, {filterType: FilterType.CreatorEmail; creator: TopCreator}>;

type BrowseFilterItem = OwnerFilterItem | ReportFilterItem | CreatorFilterItem;
type BrowseFilterSection = Section<any, NoExtension, BrowseFilterItem>;

export class BrowseFilter extends ParamComponent<BrowseFilterParams, EmptyObj, IronContextRegistry> {
  get config() {
    return {
      contexts: [`currentProject` as const, `projectTopCreators` as const],
      template: () => {
        const eventEntries = this.params.filter[FilterType.Event].map((eventName) => {
          return {type: ResourceType.Event, event: {value: eventName}, filters: []} as QueryEventEntry;
        });
        return (
          <div sel={selx(styles.browseFiltersContainer)}>
            <div sel={selx(styles.creatorFilterContainer)}>
              <div sel={selx(styles.creatorFilterLabel)}>Creators</div>
              <UserSelectorEl
                sel={selx(styles.creatorFilter)}
                params={{
                  users: this.getCreatorsList(),
                  selectedUsers: this.getSelectedCreators(),
                  icon: `profile`,
                  label: this.getSelectedCreators().length ? `Created by` : `Created by anyone`,
                  searchPlaceholder: `Search creators ...`,
                  onSelectedUsersChange: (item) => {
                    const {isSelected, value} = item;
                    const filterType = FilterType.CreatorEmail;
                    this.handleFilterChange({isSelected, value, filterType});
                  },
                  onMenuChange: (isOpen) => {
                    this.handleCreatorFilterMenuChange(isOpen);
                  },
                }}
              />
            </div>
            <ItemsMenuEl
              attrs={{multiple: true, elref: `browseFilterMenu`, 'unfocus-on-mouseleave': true}}
              props={{sections: this.getTypeFilterSections()}}
              on={{clickItem: (ev) => this.handleFilterChange(ev.detail.item)}}
            />
            {this.params.isSocialMetadataEnabled ? (
              <div sel={selx(styles.eventSection)}>
                <div sel={selx(styles.eventSectionHeader)}>Events</div>
                <QueryEntrySectionEl
                  attrs={{
                    autofocus: false,
                    'add-entry-label': `Select an event`,
                    'add-entry-button-label': `Add event`,
                    'add-entry-button-tooltip-text': `Show bookmarks using this event`,
                    'button-theme': ButtonTheme.Browse,
                    'disable-add-event-filters': true,
                    'disable-add-grouped-event-reason': `true`,
                    'disable-clone-entry': true,
                    'disable-first-time-event-filters': true,
                    'enable-event-icon': true,
                    entries: eventEntries,
                    'min-entries': 0,
                    'exclude-autotrack-events': true,
                    'exclude-dimension-properties': true,
                    'project-has-people': true,
                    'hide-events': true,
                    'project-has-cohorts': true,
                    'project-id': parseInt(getProjectId()),
                    'row-label-type': RowLabelType.None,
                    'row-types': [ResourceType.Event],
                    'user-id': this.params.userId,
                    'workspace-id': parseInt(getWorkspaceId()),
                    'is-discover-menu': true,
                    'style-override': require(`./query-entry-section.inline.styl`),
                  }}
                  on={{
                    // track: this.helpers.captureTracking,
                    update: (ev) => {
                      const eventEntries = ev.detail.entries;
                      this.params.onFilterChange({
                        ...this.params.filter,
                        [FilterType.Event]: eventEntries.map((entry) => entry.event.value),
                      });
                    },
                  }}
                />
              </div>
            ) : null}
          </div>
        );
      },
      params: {
        filter: Object,
        onFilterChange: Function,
        onTrackEvent: Function,
        isAddMode: Boolean,
        userId: Number,
        isSocialMetadataEnabled: Boolean,
      },
    };
  }

  handleFilterChange(item) {
    const {isSelected, filterType, value} = item;

    let filterValues = this.params.filter[filterType];
    if (isSelected) {
      filterValues = filterValues.filter((currVal) => currVal !== value);
    } else {
      filterValues = [...filterValues, value];
    }

    this.params.onFilterChange({
      ...this.params.filter,
      [filterType]: filterValues,
    });

    this.params.onTrackEvent?.(BrowseTrackedEvent.FilterUpdated, {
      'Changed Filter Type': filterType,
      'Changed Filter Value': value,
      'Is Filter Value Selected': !isSelected,
    });
  }

  handleCreatorFilterMenuChange(isOpen) {
    if (isOpen) {
      this.params.onTrackEvent?.(BrowseTrackedEvent.CreatorFilterMenuOpened);
    }
  }

  getTypeFilterSections(): BrowseFilterSection[] {
    return [
      {
        label: `Types`,
        items: this.getAvailableReports().map((value) => ({
          label: SearchDisplayContextDisplayName[findKeyByValue(SearchDisplayContext, value)],
          icon: getSearchIconFromDisplayContext(value),
          value,
          isSelected: this.params.filter[FilterType.Report].includes(value),
          filterType: FilterType.Report,
        })),
      },
    ];
  }

  getCreatorsList() {
    const topCreators = this.getTopCreators();
    const orderedCreatorList = uniqBy(
      [...getSuggestedCreators(userCookieUtils.getUserIdentityFromCookie()?.id, topCreators), ...topCreators],
      (creator: TopCreator) => creator.email,
    );
    orderedCreatorList.map((creator) => ({
      id: creator.id,
      email: creator.email,
      name: creator.name,
    }));
    return orderedCreatorList;
  }

  getTopCreators(): TopCreator[] {
    return this.getContext(`projectTopCreators`).getCreators();
  }

  getAvailableReports() {
    let reports = getOrderedSearchContextDisplayOptions();
    if (this.params.isAddMode) {
      reports = reports.filter((report) => {
        const reportFromContext = getReportNameFromSearchContext(report);
        return getDashboardAllowedReports().includes(reportFromContext);
      });
    }
    return reports;
  }

  getSelectedCreators() {
    return this.params.filter[FilterType.CreatorEmail] || [];
  }
}

export const BrowseFilterEl = defineAndMakeJsxFactory<BrowseFilterJsxProps>(`browse-filter`, BrowseFilter);
export const BrowseFilterReact = panel2react<BrowseFilterJsxProps>(`browse-filter`, BrowseFilter);
